import { Link } from 'react-router-dom';
import { Mock } from '../components/Mock';
import { Section } from '../components/Section';
import { clockTime, stayDate } from '../lib/format';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { SEO } from '../seo/pages';

interface Faq {
  q: string;
  a: React.ReactNode;
}

export function ContactPage() {
  usePageMeta(SEO.contact.title, SEO.contact.description, { jsonLd: SEO.contact.jsonLd });
  const { data: s } = useSettings();
  const mapsUrl = s?.hotelAddress
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.hotelAddress)}`
    : null;
  const when = s?.bookableCheckInMin
    ? `the night of ${stayDate(s.bookableCheckInMin)}`
    : 'the night of the celebration';
  const link = 'text-pink-300 underline hover:text-pink-200';

  const faqs: Faq[] = [
    {
      q: 'How do I reserve a room?',
      a: (
        <>
          Open the{' '}
          <Link to="/rooms" className={link}>
            Rooms
          </Link>{' '}
          page, pay in full by card, and your confirmation code arrives by email within a minute.
          Rooms are for {when}.
        </>
      ),
    },
    {
      q: 'Do I need to RSVP?',
      a: (
        <>
          Yes, if you are not staying at the hotel:{' '}
          <Link to="/rsvp" className={link}>
            RSVP here
          </Link>{' '}
          with the name of everyone in your party, and give your name at the door. A donation is
          welcome, never required, and all of it goes to Shawna.
        </>
      ),
    },
    {
      q: 'I have a room. Do I need to RSVP too?',
      a: 'No. A room is your RSVP. Give your name at the door; the room list is the door list.',
    },
    {
      q: 'What are check-in and check-out times?',
      a: `Check-in from ${clockTime(s?.checkInTime ?? '15:00')}, check-out by ${clockTime(s?.checkOutTime ?? '12:00')}. Early arrivals can leave bags with the front desk.`,
    },
    {
      q: 'What do I say at the front desk?',
      a: 'Give your name, the group name BrandonStrong, and the confirmation code from your email. The hotel has the room list from us, so there is nothing to pay at the desk except incidentals like the minibar or a late check-out. Bring the card you paid with in case the front desk asks to match the name.',
    },
    {
      q: 'Can I cancel or get a refund?',
      a: (
        <>
          No. Every room reservation is final and non-refundable, so the family can guarantee the
          block with the hotel, and donations made with an RSVP are final too. Full wording is in
          the{' '}
          <Link to="/terms" className={link}>
            terms
          </Link>
          .
        </>
      ),
    },
    {
      q: 'King or two queens?',
      a: 'Rooms are sold run of the house: every room is the same price and the hotel assigns a king or a two-queen layout at check-in. Both have a trundle sofa on top of the beds. Say in the special requests at checkout if you need two queens for a bigger party and the hotel will do its best.',
    },
    {
      q: 'Is there an accessible room?',
      a: 'Yes. The hotel has mobility-accessible rooms with a roll-in shower or an accessible tub, and hearing-accessible rooms with visual alerts. Ask for one in the special requests at checkout, or call the front desk with your confirmation code, and the hotel will assign it.',
    },
    {
      q: 'Parking, breakfast, Wi-Fi?',
      a: 'All included at the hotel: free self-parking in the lot, a hot breakfast in the lobby every morning from six to ten, Wi-Fi throughout the building, the outdoor pool until ten at night, and the fitness room around the clock. Every suite also has a mini fridge and microwave.',
    },
    {
      q: 'I cannot come. How else can I help?',
      a: (
        <>
          Give through the family GoFundMe on the{' '}
          <Link to="/donate" className={link}>
            Donate
          </Link>{' '}
          page. Sharing the site helps too.
        </>
      ),
    },
  ];

  return (
    <Section eyebrow="Get in touch" title="Contact & FAQ">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
        <div className="flex flex-col gap-8">
          <div>
            <p className="text-meta text-sm">Email us</p>
            {s?.contactEmail ? (
              <a
                href={`mailto:${s.contactEmail}`}
                className="mt-1 block text-xl text-ink-50 hover:text-pink-300"
              >
                {s.contactEmail}
              </a>
            ) : (
              <Mock className="mt-2">
                <p className="text-xl text-ink-50">family@brandonstrong.com</p>
              </Mock>
            )}
            <p className="mt-2 text-ink-400">
              For anything about the weekend, the site, or a reservation.
            </p>
          </div>
          <div>
            <p className="text-meta text-sm">The hotel</p>
            <p className="mt-1 text-xl text-ink-50">{s?.hotelName ?? 'Host hotel'}</p>
            {s?.hotelAddress && <p className="text-ink-200">{s.hotelAddress}</p>}
            {s?.hotelPhone && (
              <a
                href={`tel:${s.hotelPhone.replace(/[^+\d]/g, '')}`}
                className="block text-ink-200 hover:text-pink-300"
              >
                {s.hotelPhone}
              </a>
            )}
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-2 inline-block text-sm ${link}`}
              >
                Get directions
              </a>
            )}
            <p className="mt-2 text-ink-400">
              Call the hotel for room requests (floors, connecting rooms). Reservations themselves
              go through this site.
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-3xl">Questions</h2>
          <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
            {faqs.map((f) => (
              <details key={f.q} className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-sans text-lg font-semibold text-ink-50 marker:content-none">
                  {f.q}
                  <span
                    className="text-pink-500 transition-transform group-open:rotate-45"
                    aria-hidden
                  >
                    +
                  </span>
                </summary>
                <div className="faq-answer pt-3 text-ink-200">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
